import React, { useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Tooltip,
  Chip,
  IconButton,
  Divider,
} from "@mui/material";
import ChevronLeftRoundedIcon from "@mui/icons-material/ChevronLeftRounded";
import ChevronRightRoundedIcon from "@mui/icons-material/ChevronRightRounded";
import InsightsRoundedIcon from "@mui/icons-material/InsightsRounded";

import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  Tooltip as ChartTooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";

import AIIcon from "./AIIcon";
import ScatterNotesTooltip from "./ScatterNotesTooltip";
import DataSourceIcon from "../../Components/DataSourcesPanel";

/* =========================
   Helpers
========================= */
const getProductGroup = (row, fallbackIdx) =>
  row?.product_group ||
  row?.key_product_group ||
  row?.keyProductGroup ||
  row?.["product_group"] ||
  `Product ${fallbackIdx + 1}`;

const getRatingLabel = (row) =>
  String(row?.rating || row?.rating_label || "").trim();

const getNotes = (row) =>
  row?.notes || row?.note || row?.commentary || row?.comments || "";

/* =========================
   Custom Dot (color per point)
========================= */
const Dot = (props) => {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;

  const fill = payload?.fill || "rgba(255,255,255,0.9)";
  return <circle cx={cx} cy={cy} r={9} fill={fill} />;
};

const ActiveDot = (props) => {
  const { cx, cy, payload } = props;
  if (cx == null || cy == null) return null;

  const fill = payload?.fill || "rgba(255,255,255,0.95)";
  return <circle cx={cx} cy={cy} r={11} fill={fill} />;
};

const ConsultantIntelligenceScatterCard = ({
  theme,
  consultantRatings = [],
  ratingScale = {},
  ratingLabels = {},
  ratingColors = {},
  entityId,
  AiSectionFeedback,
  requestId,
  uiSectionKey,
  userEmail,
}) => {
  /* =========================
     ✅ Sort A+ → C BEFORE pagination
  ========================= */
  const sortedRatings = useMemo(() => {
    return [...(consultantRatings || [])].sort((a, b) => {
      const ra = ratingScale?.[getRatingLabel(a)];
      const rb = ratingScale?.[getRatingLabel(b)];

      // unknown ratings go last
      const aVal = ra === null || ra === undefined ? -999 : ra;
      const bVal = rb === null || rb === undefined ? -999 : rb;

      // Desc: A+ (6) → C (0)
      return bVal - aVal;
    });
  }, [consultantRatings, ratingScale]);

  /* =========================
     Pagination (10 per page)
  ========================= */
  const PAGE_SIZE = 10;
  const total = sortedRatings?.length || 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const [page, setPage] = useState(0);

  const safePage = Math.min(page, totalPages - 1);
  const startIdx = safePage * PAGE_SIZE;
  const endIdx = Math.min(startIdx + PAGE_SIZE, total);

  const visibleRatings = useMemo(
    () => sortedRatings.slice(startIdx, endIdx),
    [sortedRatings, startIdx, endIdx]
  );

  /* =========================
     Build ONE scatter dataset
     X axis is CATEGORY (product group)
     Y axis is rating scale value
  ========================= */
  const points = useMemo(() => {
    return visibleRatings
      .map((row, idx) => {
        const productGroup = getProductGroup(row, idx);
        const ratingLabel = getRatingLabel(row);
        const y = ratingScale?.[ratingLabel];

        // ✅ IMPORTANT: allow y=0 for "C"
        if (y === null || y === undefined) return null;

        return {
          productGroup,
          y,
          ratingLabel,
          notes: getNotes(row),
          fill: ratingColors?.[ratingLabel] || "rgba(255,255,255,0.9)",
        };
      })
      .filter(Boolean);
  }, [visibleRatings, ratingScale, ratingColors]);

  /* =========================
     Vertical X-Axis Tick
  ========================= */
  const VerticalTick = ({ x, y, payload }) => {
    const labelRaw = String(payload?.value || "");
    const label = labelRaw.length > 28 ? `${labelRaw.slice(0, 27)}…` : labelRaw;

    return (
      <g transform={`translate(${x},${y + 8})`}>
        <text
          textAnchor="end"
          transform="rotate(-90)"
          fill="rgba(255,255,255,0.92)"
          fontSize={11}
        >
          {label}
        </text>
      </g>
    );
  };

  const canPrev = safePage > 0;
  const canNext = safePage < totalPages - 1;

  const handlePrev = () => {
    if (!canPrev) return;
    setPage((p) => Math.max(0, p - 1));
  };

  const handleNext = () => {
    if (!canNext) return;
    setPage((p) => Math.min(totalPages - 1, p + 1));
  };

  /* =========================
     Gradient background
  ========================= */
  const primaryStart = "#003366";
  const primaryEnd = "#004080";

  /* =========================
     ✅ Legend & chips order (A+ → C)
  ========================= */
  const orderedRatings = useMemo(() => {
    const entries = Object.entries(ratingScale || {});
    entries.sort((a, b) => (b?.[1] ?? -999) - (a?.[1] ?? -999));
    return entries.map(([label]) => label);
  }, [ratingScale]);

  const renderLegend = () => {
    return (
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,

          pt: 1,
        }}
      >
        {orderedRatings.map((r) => (
          <Box
            key={r}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.8,
              px: 1.2,
              py: 0.6,
              borderRadius: 999,
              border: "1px solid rgba(255,255,255,0.18)",
              bgcolor: "rgba(255,255,255,0.06)",
              backdropFilter: "blur(6px)",
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: ratingColors?.[r] || "rgba(255,255,255,0.7)",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: 800,
                color: "rgba(255,255,255,0.92)",
              }}
            >
              {r}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  };

  return (
    <Card
      sx={{
        flex: 1,
        borderRadius: 3,
        overflow: "hidden",
        boxShadow: "0 18px 35px rgba(15,23,42,0.28)",
        background: `linear-gradient(135deg, ${primaryStart}, ${primaryEnd})`,
      }}
    >
      <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: 2,
            alignItems: "flex-start",
            mb: 1.5,
          }}
        >
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <InsightsRoundedIcon sx={{ color: "rgba(255,255,255,0.92)" }} />
              <Tooltip
                title="Consultant positioning across product groups (hover points for notes)."
                arrow
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 900,
                    fontSize: "1.05rem",
                    color: "rgba(255,255,255,0.95)",
                    cursor: "help",
                    letterSpacing: 0.2,
                  }}
                >
                  Consultant Intelligence
                </Typography>
              </Tooltip>
              <AIIcon mode="dark" />
              <DataSourceIcon mode="dark" sources={["Salesforce", "Nasdaq"]} />
            </Box>

            <Typography
              sx={{
                fontSize: "0.82rem",
                color: "rgba(255,255,255,0.78)",
                mt: 0.35,
              }}
            >
              Ratings by product group. Hover a point to read consultant notes.
            </Typography>
          </Box>

          <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
            <AiSectionFeedback
              sectionName="ClientEngagement - Consultant Intelligence (AI)"
              entityId={entityId}
              variant="dark"
              requestId={requestId}
              uiSectionKey={uiSectionKey}
              userEmail={userEmail}
            />
          </Box>
        </Box>

        {/* Controls row: chips + pagination */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "stretch", md: "center" },
            justifyContent: "space-between",
            gap: 1.25,
            mb: 1.6,
          }}
        >
          {/* Rating chips */}

          {/* Pagination */}
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "0.82rem",
                color: "rgba(255,255,255,0.78)",
                mr: 1,
              }}
            >
              {total ? (
                <>
                  Showing <b>{startIdx + 1}</b>–<b>{endIdx}</b> of{" "}
                  <b>{total}</b>
                </>
              ) : (
                "No data"
              )}
            </Typography>

            <IconButton
              onClick={handlePrev}
              disabled={!canPrev}
              size="small"
              sx={{
                border: "1px solid rgba(255,255,255,0.18)",
                bgcolor: "rgba(255,255,255,0.06)",
                color: "rgba(255,255,255,0.92)",
                backdropFilter: "blur(6px)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.10)" },
              }}
            >
              <ChevronLeftRoundedIcon />
            </IconButton>

            <Box
              sx={{
                px: 1.25,
                py: 0.6,
                borderRadius: 999,
                fontSize: "0.78rem",
                fontWeight: 900,
                color: "rgba(255,255,255,0.92)",
                bgcolor: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.18)",
                backdropFilter: "blur(6px)",
                minWidth: 108,
                textAlign: "center",
              }}
            >
              Page {safePage + 1} / {totalPages}
            </Box>

            <IconButton
              onClick={handleNext}
              disabled={!canNext}
              size="small"
              sx={{
                border: "1px solid rgba(255,255,255,0.18)",
                bgcolor: "rgba(255,255,255,0.06)",
                color: "rgba(255,255,255,0.92)",
                backdropFilter: "blur(6px)",
                "&:hover": { bgcolor: "rgba(255,255,255,0.10)" },
              }}
            >
              <ChevronRightRoundedIcon />
            </IconButton>
          </Box>
        </Box>
        {/* Rating Legend */}
        <Box sx={{ mb: 1.5 }}>{renderLegend()}</Box>

        <Divider sx={{ borderColor: "rgba(255,255,255,0.12)", mb: 1.8 }} />

        {/* Chart panel (glass) */}
        <Box
          sx={{
            borderRadius: 3,
            p: { xs: 1.5, md: 2 },
            bgcolor: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.14)",
            backdropFilter: "blur(10px)",
          }}
        >
          <Box sx={{ height: 520 }}>
            {points.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <ScatterChart
                  margin={{ top: 18, right: 8, bottom: 30, left: 8 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="rgba(255,255,255,0.18)"
                  />

                  <XAxis
                    dataKey="productGroup"
                    type="category"
                    interval={0}
                    tick={<VerticalTick />}
                    height={120}
                    axisLine={{ stroke: "rgba(255,255,255,0.55)" }}
                    tickLine={{ stroke: "rgba(255,255,255,0.55)" }}
                  />

                  <YAxis
                    dataKey="y"
                    type="number"
                    allowDecimals={false}
                    domain={[-0.4, 6.4]}
                    ticks={[0, 1, 2, 3, 4, 5, 6]}
                    tick={{ fill: "rgba(255,255,255,0.9)", fontSize: 12 }}
                    tickFormatter={(v) => ratingLabels?.[v] || v}
                    padding={{ top: 12, bottom: 18 }}
                    axisLine={{ stroke: "rgba(255,255,255,0.55)" }}
                    tickLine={{ stroke: "rgba(255,255,255,0.55)" }}
                  />

                  <ChartTooltip
                    content={<ScatterNotesTooltip />}
                    cursor={{
                      stroke: "rgba(255,255,255,0.25)",
                      strokeWidth: 1,
                    }}
                  />

                  <Scatter
                    name="Ratings"
                    data={points}
                    shape={<Dot />}
                    activeShape={<ActiveDot />}
                    isAnimationActive={false}
                  />
                </ScatterChart>
              </ResponsiveContainer>
            ) : (
              <Box
                sx={{
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  fontSize: "0.9rem",
                  color: "rgba(255,255,255,0.85)",
                }}
              >
                No consultant rating data available yet.
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default ConsultantIntelligenceScatterCard;
